import { BillDetailed, BillVersion } from '@enview/interface/types/bills/Bill';
import { InsightsPolicyTopicType } from '@enview/interface/types/bills/InsightsPolicyTopic';
import { t } from 'i18next';
import moment from 'moment';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import {
  trackBillSummary,
  trackBillVersionToVersionSummaryViewed,
} from '../../analytics/BillAnalytics';
import Info from '../../components/svg/InfoCircleIcon';
import Sparkles from '../../components/svg/Sparkles';
import { g as abilityGlossary } from '../../config/ability';
import { checkPermission } from '../../dux';
import { State, Thunk } from '../../dux/@types';
import getJurisdictionAdapter from '../../helpers/jurisdictions';
import BillAutoSummary from '../Shared/BillInsights/BillAutoSummary';
import BillVersionToVersionAutoSummary from '../Shared/BillInsights/BillVersionToVersionAutoSummary';
import {
  isJurisdictionSupportedForAISummary,
  AISummaryType,
} from '../Shared/BillInsights/utils';
import SingleSelectDropDown, {
  OptionType,
} from '../Shared/DropDowns/SingleSelectDropDown';
import GateTooltip, { GateTooltipFeature } from '../Shared/Tooltips/TooltipGate';
import PremiumSubscriptionPromoModal from '../../components/PremiumSubscriptionPromoModal';
import { BillAPI } from '../../api';
import { Spinner } from 'react-bootstrap';

type BillAiFeaturesProps = {
  bill: BillDetailed;
};

const BillAiFeatures = (props: BillAiFeaturesProps): ReactElement => {
  const { bill } = props;

  const dispatch = useDispatch();
  const [showAutoSummary, setShowAutoSummary] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showVersionToVersionAutoSummary, setShowVersionToVersionAutoSummary] =
    useState<boolean>(false);

  const [triggerTrackSummary, { data: summariesGenerated, isLoading }] =
    BillAPI.endpoints.getBillSummariesGenerated.useLazyQuery();

  const jurisdictionAdapter = getJurisdictionAdapter(bill.session.state.abbreviation);
  const sortedBillVersions: BillVersion[] = useMemo(
    () =>
      bill.versions
        ? jurisdictionAdapter.constructor.sortBillVersions(bill.versions)
        : [],
    [bill.versions, jurisdictionAdapter.constructor],
  );

  const canViewSummary = (): Thunk<boolean> => {
    return dispatch(
      checkPermission(abilityGlossary.VIEW, abilityGlossary.BILL_SUMMARY, true),
    );
  };

  const hasRestrictedAccessToSummaries = summariesGenerated && summariesGenerated.limit;

  const canViewVersionToVersionSummary = (): Thunk<boolean> => {
    return dispatch(
      checkPermission(
        abilityGlossary.VIEW,
        abilityGlossary.BILL_VERSION_TO_VERSION_SUMMARY,
        true,
      ),
    );
  };

  const orgUser = useSelector((state: State) => state.account.organizationUser);

  useEffect(() => {
    if (canViewSummary()) {
      triggerTrackSummary({ billId: bill.id });
    }
  }, [triggerTrackSummary, canViewSummary()]);

  const handleGenerateBillAutoSummary = () => {
    if (!canViewSummary()) return;
    if (!showAutoSummary) {
      trackBillSummary(bill.id, 'Details');
    }
    const isFreshSummary = !summariesGenerated?.summariesGenerated.includes(bill.id);
    if (
      summariesGenerated &&
      isFreshSummary &&
      hasRestrictedAccessToSummaries &&
      summariesGenerated.summariesGenerated.length >= summariesGenerated.limit
    ) {
      setShowModal(true);
      return;
    }

    triggerTrackSummary({ billId: bill.id });
    setShowAutoSummary(!showAutoSummary);
  };

  const insightTopics =
    bill.insightTopics &&
    bill.insightTopics.filter(
      (topic) => topic.policyTopic != InsightsPolicyTopicType.UNTAGGABLE,
    );

  // Select introduced version for auto bill summary
  const versionIdsAscending = bill.versions
    ? bill.versions.map((bv) => bv.id).sort((a, b) => a - b)
    : [];
  const introducedVersionByName = bill.versions
    ? bill.versions.find((bv) => bv.versionName.toLowerCase().includes('introduc'))
    : false;
  let introducedVersionId = 0;
  if (versionIdsAscending.length > 0)
    introducedVersionId = introducedVersionByName
      ? introducedVersionByName.id
      : versionIdsAscending[0];

  // set size check for Bill Details tab specifically
  // const isSmall = window.matchMedia('(max-width: 1130px)').matches;

  const [billVersionA, setBillVersionA] = useState<number | undefined>(
    sortedBillVersions?.at(-2)?.id,
  );
  const [billVersionB, setBillVersionB] = useState<number | undefined>(
    sortedBillVersions?.at(-1)?.id,
  );
  const [billSummaryVersion, setBillSummaryVersion] = useState<number | undefined>(
    sortedBillVersions?.at(-1)?.id,
  );

  const versionOptions: OptionType<number>[] = sortedBillVersions
    ? sortedBillVersions.map((version) => ({
        label: version.versionPostedDate
          ? `${version.versionName} (${moment
              .utc(version.versionPostedDate)
              .format('M/D/YYYY')})`
          : version.versionName,
        value: version.id,
      }))
    : [];

  const changeBillVersionA = (option: OptionType<number>): void => {
    setBillVersionA(option.value);
  };

  const changeBillVersionB = (option: OptionType<number>): void => {
    setBillVersionB(option.value);
  };

  const changeBillSummaryVersion = (option: OptionType<number>): void => {
    setShowAutoSummary(false);
    setBillSummaryVersion(option.value);
  };

  const hasInsightTopics = useMemo(() => {
    return insightTopics && insightTopics.length > 0;
  }, [insightTopics]);

  const hasSummary = useMemo(() => {
    return (
      isJurisdictionSupportedForAISummary(
        bill.session.state.abbreviation,
        AISummaryType.IntroducedVersionSummary,
        orgUser,
      ) &&
      bill.versions &&
      bill.versions.length > 0
    );
  }, [bill.versions, bill.session.state.abbreviation]);

  const hasVersionToVersionChanges = useMemo(() => {
    return (
      isJurisdictionSupportedForAISummary(
        bill.session.state.abbreviation,
        AISummaryType.VersionToVersionSummary,
        orgUser,
      ) &&
      bill.versions &&
      bill.versions.length > 1
    );
  }, [bill.versions, bill.session.state.abbreviation]);

  const showAiSidebar = useMemo(() => {
    return hasInsightTopics || hasSummary || hasVersionToVersionChanges;
  }, [hasInsightTopics, hasSummary, hasVersionToVersionChanges]);

  const billInsightTopicsSection = (
    <>
      {hasInsightTopics && (
        <div className="detail-item insight-topics">
          <span className="d-flex flex-row align-items-center">
            <h3 className="title mr-2">
              {t('bill.sidebar.insightBillTopics.topicsHeader')}
            </h3>
            <Info id="bill-topics-tooltip-detail" size={18} />
            <ReactTooltip
              anchorId="bill-topics-tooltip-detail"
              className="tooltip-content #tooltip-placement"
              clickable
              delayHide={500}
              place="top"
              style={{ maxWidth: '300px' }}
            >
              <Trans
                components={{
                  insightsBillTopicsLink: (
                    <a href={t('bill.sidebar.insightBillTopics.url')} target="_blank" />
                  ),
                  gate: <p className="tooltip-header" />,
                }}
                i18nKey="bill.sidebar.insightBillTopics.tooltipText"
              />
            </ReactTooltip>
          </span>
          {insightTopics?.map((topic) => <div key={topic.id}>{topic.policyTopic}</div>)}
        </div>
      )}
    </>
  );

  const autoSummarySection = (
    <>
      {hasSummary && (
        <div className={`detail-item ai-bill-summary`}>
          <span className={`${'d-flex flex-row'}`}>
            <h3 className="title">{t('bill.sidebar.billSummary.summaryHeader')}</h3>
            <span className="ml-1 mt-1" id="bill-summary-tooltip-detail">
              <Info size={18} />
            </span>
            <ReactTooltip
              anchorId="bill-summary-tooltip-detail"
              children={t('bill.sidebar.billSummary.tooltipText')}
              className="tooltip-content"
              clickable
              delayHide={500}
              place="bottom"
              style={{ maxWidth: '300px' }}
            />
          </span>
          {hasRestrictedAccessToSummaries && (
            <div className="promo-text">
              <Trans
                components={{
                  upgradeLink: (
                    <a
                      className="upgrade-link"
                      href={t('urls.upgradeAccount')}
                      target="blank"
                    />
                  ),
                }}
                i18nKey="bill.sidebar.billSummary.premiumPromo"
                key="bill.sidebar.billSummary.premiumPromo"
              />
            </div>
          )}
          {
            <div className="filter w-50">
              <SingleSelectDropDown
                defaultLabel={t('bill.header.billVersionDefaultLabel')}
                onSelect={changeBillSummaryVersion}
                options={versionOptions}
                selectedValue={billSummaryVersion}
              />
            </div>
          }
          <div className="generate-btn-group">
            <button
              className={'btn-tertiary'}
              disabled={canViewSummary() && (isLoading || !summariesGenerated)}
              id="generate"
              onClick={handleGenerateBillAutoSummary}
              type="button"
            >
              <span>
                {showAutoSummary
                  ? t('bill.sidebar.billSummary.buttonText.hide')
                  : t('bill.sidebar.billSummary.buttonText.generate')}
              </span>
            </button>
            {hasRestrictedAccessToSummaries && (
              <div className="summaries-count ml-3">
                {!isLoading && summariesGenerated ? (
                  <>
                    <Sparkles className="mr-2" height="0.85rem" width="0.85rem" />
                    <p>{`${summariesGenerated.summariesGenerated.length}/${summariesGenerated?.limit} AI summaries generated`}</p>
                  </>
                ) : (
                  <Spinner
                    animation="border"
                    className="text-field-spinner"
                    role="status"
                    size="sm"
                    variant="secondary"
                  />
                )}
              </div>
            )}
          </div>
          {!canViewSummary() && (
            <GateTooltip
              accountType="pro"
              anchorId="generate"
              customContent={
                <Trans
                  components={{
                    gate: <p className="tooltip-header" />,
                    aiLink: <a href={t('urls.createAccount')} />,
                  }}
                  i18nKey="featureGating.tooltipText.aiBillSummary"
                  key="featureGating.tooltipText.aiBillSummary"
                />
              }
              featureName={GateTooltipFeature.BillSummary}
              place="bottom"
            />
          )}
          {showAutoSummary && (
            <BillAutoSummary
              billId={bill.id}
              versionId={billSummaryVersion ?? introducedVersionId}
            />
          )}
        </div>
      )}
    </>
  );

  const versionToVersionAutoSummarySection = (
    <>
      {hasVersionToVersionChanges && (
        <div className="detail-item version-to-version">
          <div className="d-flex flex-row align-items-center">
            <span className="title mr-2">
              {t('bill.sidebar.versionToVersionChanges.header')}
            </span>
            <Info id="bill-version-to-version-tooltip-detail" size={18} />
            <ReactTooltip
              anchorId="bill-version-to-version-tooltip-detail"
              children={t('bill.sidebar.versionToVersionChanges.tooltipText')}
              className="tooltip-content"
              clickable
              delayHide={500}
              place="bottom"
              style={{ maxWidth: '300px' }}
            />
          </div>
          <div className="d-flex dropdown-container mt-1">
            <div className="filter">
              <SingleSelectDropDown
                defaultLabel={t('bill.header.billVersionDefaultLabel')}
                onSelect={changeBillVersionA}
                options={versionOptions}
                selectedValue={billVersionA}
              />
            </div>
            <span className="to-text px-2">
              {t('bill.sidebar.versionToVersionChanges.toText')}
            </span>
            <div className="filter">
              <SingleSelectDropDown
                defaultLabel={t('bill.header.billVersionDefaultLabel')}
                onSelect={changeBillVersionB}
                options={versionOptions}
                selectedValue={billVersionB}
              />
            </div>
          </div>
          <div className="generate-btn-group">
            <button
              className={'btn-tertiary'}
              id="ai-bill-version-diff-generate"
              onClick={() => {
                if (canViewVersionToVersionSummary()) {
                  if (!showVersionToVersionAutoSummary) {
                    trackBillVersionToVersionSummaryViewed(
                      bill.id,
                      billVersionA ?? 0,
                      billVersionB ?? 0,
                    );
                  }
                  setShowVersionToVersionAutoSummary(!showVersionToVersionAutoSummary);
                }
              }}
              type="button"
            >
              <span>
                {showVersionToVersionAutoSummary
                  ? t('bill.sidebar.billSummary.buttonText.hide')
                  : t('bill.sidebar.billSummary.buttonText.generate')}
              </span>
            </button>
          </div>
          {!canViewVersionToVersionSummary() && (
            <GateTooltip
              accountType="pro"
              anchorId="ai-bill-version-diff-generate"
              customContent={
                <Trans
                  components={{
                    aiLink: (
                      <a
                        href={t('urls.aiBillVersionToVersionChanges')}
                        target="_blank"
                      />
                    ),
                    gate: <p className="tooltip-header" />,
                  }}
                  i18nKey="featureGating.tooltipText.billVersionToVersionChanges"
                  key="featureGating.tooltipText.billVersionToVersionChanges"
                />
              }
              featureName={GateTooltipFeature.BillSummary}
              place="bottom"
            />
          )}
          {showVersionToVersionAutoSummary && (
            <BillVersionToVersionAutoSummary
              billId={bill.id}
              billVersionAId={billVersionA ?? 0}
              billVersionBId={billVersionB ?? 0}
            />
          )}
        </div>
      )}
    </>
  );

  return !showAiSidebar ? (
    <></>
  ) : (
    <>
      <div className="ai-sidebar-features">
        <div className="ai-title-group">
          <Sparkles className="mr-2" height="18px" width="18px" />
          <h3 className="ai-title">{t('bill.sidebar.aiHeader')}</h3>
        </div>
        <div className="feature-section">
          {billInsightTopicsSection}
          {autoSummarySection}
          {versionToVersionAutoSummarySection}
        </div>
      </div>
      <PremiumSubscriptionPromoModal
        modalIsOpen={showModal}
        setModalIsOpen={setShowModal}
      />
    </>
  );
};

export default BillAiFeatures;
